import React from 'react';
import { Typography, Flex, Button, theme } from 'antd';
import { EyeInvisibleOutlined } from '@ant-design/icons';

import { LegendLabelSquare } from '~components/Charts';

function ClickableWrapper({ children, onClick }: React.PropsWithChildren<{ onClick?: () => void }>) {
  if (onClick) {
    return (
      <Button onClick={onClick} type="text" style={{ height: 'auto' }}>
        {children}
      </Button>
    );
  }
  return <>{children}</>;
}

type MainChartLegendLabelBigProps = {
  value: string;
  label: string;
  labelColor: string;
  tooltipContent?: string;
  crossedOut?: boolean;
  onClick?: () => void;
};

function MainChartLegendLabelBig({
  value,
  label,
  labelColor,
  tooltipContent,
  crossedOut,
  onClick,
}: MainChartLegendLabelBigProps) {
  const { token } = theme.useToken();

  return (
    <ClickableWrapper onClick={onClick}>
      <Flex gap={token.sizeXXS} vertical style={{ padding: `${token.paddingXXS}px ${token.paddingXS}px` }}>
        <Flex align="center" gap={token.sizeXS}>
          <Typography.Text
            strong
            style={{
              fontSize: token.fontSizeHeading2,
              wordBreak: 'keep-all',
            }}
          >
            {crossedOut ? <EyeInvisibleOutlined aria-label="icon-eye-invisible" /> : value}
          </Typography.Text>
        </Flex>
        <LegendLabelSquare color={labelColor} label={label} tooltipContent={tooltipContent} crossedOut={crossedOut} />
      </Flex>
    </ClickableWrapper>
  );
}

type MainChartLegendLabelProps = MainChartLegendLabelBigProps & {
  secondaryLabel?: string;
};

function MainChartLegendLabel({
  value,
  label,
  labelColor,
  secondaryLabel,
  tooltipContent,
  crossedOut,
  onClick,
}: MainChartLegendLabelProps) {
  const { token } = theme.useToken();

  return (
    <ClickableWrapper onClick={onClick}>
      <Flex
        justify="flex-end"
        gap={token.sizeXXS}
        vertical
        style={{ padding: `${token.paddingXXS}px ${token.paddingXS}px` }}
      >
        <span>
          <Typography.Text
            strong
            style={{
              fontSize: token.fontSizeHeading3,
              color: token.colorTextTertiary,
              wordBreak: 'keep-all',
            }}
          >
            {crossedOut ? <EyeInvisibleOutlined aria-label="icon-eye-invisible" /> : value}
          </Typography.Text>
          {secondaryLabel && !crossedOut && <Typography.Text type="secondary"> {secondaryLabel}</Typography.Text>}
        </span>
        <LegendLabelSquare color={labelColor} label={label} tooltipContent={tooltipContent} crossedOut={crossedOut} />
      </Flex>
    </ClickableWrapper>
  );
}

export function MainChartLegend({ children }: React.PropsWithChildren) {
  const { token } = theme.useToken();
  return (
    <Flex style={{ marginBottom: token.marginXL }} gap={token.sizeSM} align="flex-end" wrap="wrap">
      {children}
    </Flex>
  );
}

MainChartLegend.Label = MainChartLegendLabel;
MainChartLegend.LabelBig = MainChartLegendLabelBig;

export default MainChartLegend;
